import ReactPDF, { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Section, Title } from './Section';
import { IconText } from './Badge';
import { FeaturesSection } from './FeaturesSection';
import { GallerySection } from './GallerySection';
import { PaymentPlanSection } from './PaymentPlanSection';

import {
  BuildingsBlue,
  CalendarBlue,
  FloorBlue,
  FurnishingBlue,
  LocationIcon,
  PaintRollerBlue,
  ParkingBlue,
} from '@/assets/pdf';

import { AddressIconText } from './AddressIconText';
import { ContactSection } from './ContactSection';
import { TopGallery } from './TopGallery';
import { IOffplanPdfDocument } from '@/types/pdf';
import { UnitCard } from './UnitCard';
import { splitAndReformat } from './UnitsPdfDocument';
import { IUnit } from '@/types/offplans';
import { useMemo } from 'react';

ReactPDF.Font.register({
  family: 'Poppins',
  src: '/assets/fonts/Poppins-Regular.ttf',
});
ReactPDF.Font.register({
  family: 'Poppins',
  fontWeight: 'bold',
  src: '/assets/fonts/Poppins-Bold.ttf',
});
type GroupedUnits = {
  [key: string]: IUnit[];
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Poppins',
    fontSize: 40,
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
  },
  bold: {
    fontFamily: 'Poppins',
    fontWeight: 'bold',
    fontSize: 40,
  },
  price: {
    fontFamily: 'Poppins',
    fontWeight: 'bold',
    fontSize: 56,
    lineHeight: 1.3,
  },
  inline: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 32,
    color: '#1650FF',
    marginBottom: 64,
  },
  pageTitle: {
    display: 'flex',
    flexDirection: 'row',
    color: '#2A3842',
    fontFamily: 'Poppins',
    fontWeight: 'bold',
    fontSize: 32,
    backgroundColor: '#1650FF66',
    borderRadius: '10px',
    padding: 23,
  },
  pageTitleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    margin: '58 0 20 0',
  },
  characteristicsContainer: {
    backgroundColor: '#F1F7FF',
    padding: '24px',
    borderRadius: '8px',
    width: '48vw',
  },
  characteristicsMainContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
  },
  characteristicsInnerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  characteristicsText: {
    color: '#2A3842',
    opacity: 0.5,
  },
});

export function OffplanPdfDocument({
  showContent,
  offplanData,
  personalInfo,
  currentAgencyInfo,
  units,
}: IOffplanPdfDocument) {
  const groupAndSortUnits = (units: IUnit[] | undefined) => {
    if (!units) return {};

    const groupedUnits = units.reduce<GroupedUnits>((acc, unit) => {
      const beds = unit.beds;
      if (!acc[beds]) {
        acc[beds] = [];
      }
      acc[beds].push(unit);
      return acc;
    }, {});

    Object.keys(groupedUnits).forEach((beds) => {
      groupedUnits[beds].sort((a, b) => a.price - b.price);
    });

    return groupedUnits;
  };

  const characteristics = [
    {
      name: 'Developent status',
      value: offplanData?.developmentStatus
        ? splitAndReformat(offplanData?.developmentStatus)
        : null,
      icon: PaintRollerBlue,
    },
    {
      name: 'Property type',
      value: offplanData?.constructionType ? splitAndReformat(offplanData?.constructionType) : null,
      icon: BuildingsBlue,
    },
    {
      name: 'Floor',
      value: offplanData?.numberOfFloors,
      icon: FloorBlue,
    },
    {
      name: 'Handover',
      value: offplanData?.handover,
      icon: CalendarBlue,
    },
    {
      name: 'Furnishing',
      value: offplanData?.furnishing ? 'Yes' : 'No',
      icon: FurnishingBlue,
    },
    {
      name: 'Parking',
      value: offplanData?.coveredParking ? 'Yes' : 'No',
      icon: ParkingBlue,
    },
  ];
  const splitPoint = Math.ceil(characteristics.length / 2);

  const firstHalf = characteristics.slice(0, splitPoint);
  const secondHalf = characteristics.slice(splitPoint);

  const paymentPlan = offplanData?.paymentPlan;
  const renderCharacteristics = (chars: any[]) =>
    chars.map((character, index, arr) => (
      <View key={character.name} style={styles.characteristicsContainer}>
        <View style={styles.characteristicsInnerContainer}>
          <IconText iconSrc={character.icon}>{character.name}</IconText>
          <Text style={styles.characteristicsText}> {character.value}</Text>
        </View>
        {arr.length > index + 1 && <Viewider />}
      </View>
    ));

  return (
    <Document>
      <Page size='A4' dpi={300} style={styles.page}>
        <View style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          <View style={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>
            <TopGallery wrap={true} images={showContent.images} />
            <View>
              <View style={styles.pageTitleWrapper}>
                <Text style={styles.pageTitle}>{offplanData?.title || 'a'}</Text>
              </View>
              <AddressIconText iconSrc={LocationIcon}>{offplanData?.nameEn}</AddressIconText>
            </View>
            <View style={styles.characteristicsMainContainer}>
              <View>{renderCharacteristics(firstHalf)}</View>
              <View>{renderCharacteristics(secondHalf)}</View>
            </View>
            <View style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}></View>
            <Section wrap={true}>
              <Text>{offplanData?.description}</Text>
            </Section>

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: '40px' }}>
            {Object.entries(groupAndSortUnits(units)).map(([beds, units]) => (
              <View key={beds}>
                <Text
                  style={{
                    fontSize: 68,
                    fontWeight: 'bold',
                    color: '#1650FF',
                    marginVertical: 10,
                  }}
                >
                  {beds} Room unit
                </Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: '40px' }}>
                  {units.map((unit) => (
                    <UnitCard key={unit.id} unit={unit} offplanData={offplanData} showPrice={showContent.showPrice} />
                  ))}
                </View>
              </View>
            ))}
            </View>
          </View>
          <FeaturesSection data={offplanData} />
          {paymentPlan ? <PaymentPlanSection paymentPlan={paymentPlan} /> : null}

          <View
            wrap={false}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '48px',
            }}
          >
            {(showContent.isBrokersInformationVisible ||
              showContent.isAgencyInformationVisible) && <Title>Contacts</Title>}
            {showContent.isBrokersInformationVisible && (
              <ContactSection contactInfo={personalInfo} />
            )}
            {showContent.isAgencyInformationVisible && (
              <ContactSection contactInfo={currentAgencyInfo} />
            )}
          </View>

          <GallerySection wrap={true} title='Gallery' images={showContent.images} />
        </View>
      </Page>
    </Document>
  );
}


const Viewider = () => (
  <View
    style={{
      width: '100%',
      height: 2,
      backgroundColor: '#2A384233',
      marginHorizontal: 0,
    }}
  />
);
